import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  A11y,
  Autoplay,
  FreeMode,
  Mousewheel,
  Pagination,
  Navigation,
  Scrollbar
} from 'swiper';
import { Button, Typography } from '@one-thd/sui-atomic-components';
import { Paginator } from './Paginator';

const SCREEN_SM = 640;
const SCREEN_MD = 768;
const SCREEN_LG = 1024;
const SCREEN_XL = 1280;
const SCREEN_2XL = 1440;

const PEEKVALUES = {
  sm: 0.2,
  md: 0.4,
  lg: 0.6
};
const DEFAULT_BREAKPOINTS = {
  sm: {
    slidesPerView: 3,
    slidesPerGroup: 3
  },
  md: {
    slidesPerView: 4,
    slidesPerGroup: 4
  },
  lg: {
    slidesPerView: 5,
    slidesPerGroup: 5
  },
  xl: {
    slidesPerView: 6,
    slidesPerGroup: 6
  },
  '2xl': {
    slidesPerView: 7,
    slidesPerGroup: 7
  }
};

/**
 * `Carousel` allows multiple pieces of content to occupy a single space on a screen.
 *
 * It usually contains `Card`s as its children, but can also contain other components like native
 * `img` or `picture` elements.
 *
 * `Carousel` needs to be imported from the `@one-thd/sui-carousel` package:
 *
 * Related components: [Card](#card)
 *
 * ```jsx
 * import { Carousel } from '@one-thd/sui-carousel';
 * ```
 */
const V2Carousel = (props) => {
  const {
    type = 'peek',
    action,
    autoplay = false,
    breakpoints = DEFAULT_BREAKPOINTS,
    buttonSpacing = '4',
    containerId,
    children = [],
    disableMargin = false,
    disableFreeMode = false,
    hideControls = false,
    hideBulletsBackground = false,
    loop,
    paginationPosition = 'inside',
    peek = 'sm',
    scrollbarPosition = 'outside',
    shadow,
    slidesPerGroup = 1,
    slidesPerView = type === 'peekless' ? 1 : 2,
    spaceBetween = 16,
    SwiperProps,
    swiperStyles = 'reset',
    title: titleProp,
  } = props;

  const containerRef = useRef(null);
  const nextButtonRef = useRef(null);
  const isHero = type === 'peekless' || type === 'hero';
  const peekAmount = isHero ? 0 : PEEKVALUES[peek];

  const rootId = swiperStyles === 'default' ? 'sui-carousel-defaults' : 'sui-carousel-resets';

  const _breakpoints = isHero ? {} : {
    [SCREEN_SM]: {
      slidesPerView: breakpoints?.sm ? breakpoints.sm.slidesPerView + peekAmount : 0,
      slidesPerGroup: breakpoints?.sm?.slidesPerGroup,
      spaceBetween: breakpoints?.sm?.spaceBetween || spaceBetween
    },
    [SCREEN_MD]: {
      slidesPerView: breakpoints?.md ? breakpoints.md.slidesPerView + peekAmount : 0,
      slidesPerGroup: breakpoints?.md?.slidesPerGroup,
      spaceBetween: breakpoints?.md?.spaceBetween || spaceBetween
    },
    [SCREEN_LG]: {
      slidesPerView: breakpoints?.lg ? breakpoints.lg.slidesPerView + peekAmount : 0,
      slidesPerGroup: breakpoints?.lg?.slidesPerGroup,
      spaceBetween: breakpoints?.lg?.spaceBetween || spaceBetween
    },
    [SCREEN_XL]: {
      slidesPerView: breakpoints?.xl ? breakpoints.xl.slidesPerView + peekAmount : 0,
      slidesPerGroup: breakpoints?.xl?.slidesPerGroup,
      spaceBetween: breakpoints?.xl?.spaceBetween || spaceBetween
    }
  };

  if (breakpoints?.['2xl'] && !isHero) {
    _breakpoints[SCREEN_2XL] = {
      slidesPerView: breakpoints?.['2xl'] ? breakpoints['2xl'].slidesPerView + peekAmount : 0,
      slidesPerGroup: breakpoints?.['2xl']?.slidesPerGroup,
      spaceBetween: breakpoints?.['2xl']?.spaceBetween || spaceBetween
    };
  }

  useEffect(() => {
    if (containerId) {
      const container = document.getElementById(containerId);
      if (container) {
        container.style.overflow = 'hidden';
      }
    }
  }
  , [containerId]);

  const onSwiperSlideKeyUp = ({ key, currentTarget }, index) => {
    if (key === 'Enter' && currentTarget !== document.activeElement) {
      return;
    }
    const cardAnchor = currentTarget?.querySelector('a[tabindex="0"],button');
    if (key === 'ArrowRight' && index !== children.length - 1) {
      currentTarget?.nextSibling?.querySelector('a[tabindex="0"],button')?.focus();
    } else if (key === 'ArrowLeft' && index !== 0) {
      currentTarget?.previousSibling?.querySelector('a[tabindex="0"],button')?.focus();
    } else if ((key === ' ' || key === 'Enter') && cardAnchor) {
      cardAnchor.click();
    }
  };

  const focusNextElement = () => {
    // Elements to consider within selection
    let focusableElements = 'a:not([disabled]), button:not([disabled]), input:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';
    if (document.activeElement) {
      let elements = Array.prototype.filter.call(
        document.querySelectorAll(focusableElements),
        function (element) {
          // Visibility check for available elements
          return (element);
        }
      );
      const currentIndex = Array.prototype.indexOf.call(elements, nextButtonRef.current);
      if (currentIndex > -1) {
        const nextElement = elements[currentIndex + 2] || elements[0];
        nextElement.focus();
      }
    }
  };

  let title = titleProp;
  if (title != null && title?.type?.displayName !== 'Typography') {
    title = <Typography variant="h2">{title}</Typography>;
  }

  const header = (
    <>
      {title != null || action != null ? (
        <div className="sui-flex sui-justify-between">
          {title}
          {action ? <div className="sui-m-2">{action}</div> : null}
        </div>
      ) : null}
    </>
  );

  const scrollbarSettings = (!loop && !isHero) && {
    draggable: true,
    hide: true,
    dragClass: [(scrollbarPosition === 'outside' ? 'sui-mt-1' : '-sui-mt-1'), 'sui-bg-medium', 'sui-h-1', 'sui-cursor-pointer', 'sui-rounded-lg'],
    horizontalClass: 'sui-swiper-scrollbar',
    snapOnRelease: false
  };

  const paginationBulletClasses = 'sui-swiper-pagination-bullet-actives-prev-prev sui-swiper-pagination-bullet-actives-prev sui-swiper-pagination-bullet-actives-main sui-swiper-pagination-bullet-actives-next sui-swiper-pagination-bullet-actives-next-next';

  // These pagination settings are using our own utility classes
  const paginationSettings = isHero && {
    bulletClass: 'sui-swiper-pagination-bullet-core',
    bulletActiveClass: 'sui-swiper-pagination-bullet-actives',
    clickable: true,
    dynamicBullets: true,
    dynamicMainBullets: 1,
    horizontalClass: 'sui-swiper-pagination-core',
    modifierClass: 'sui-swiper-pagination-'
  };

  /* const paginationSettings = isHero && {
    bulletClass: 'sui-swiper-pagination-bullet',
    clickable: true,
    dynamicBullets: true,
    dynamicMainBullets: 1,
    horizontalClass: hideBulletsBackground ? 'sui-bg-transparent' : 'sui-swiper-pagination',
  }; */

  const freeModeSettings = !isHero && {
    enabled: !disableFreeMode,
    minimumVelocity: 0.1,
    momentumBounce: false,
    momentumBounceRatio: 0.5,
    momentumRatio: 0.7,
    momentumVelocityRatio: 1.2,
    sticky: false
  };

  // Keep scrollbar always visible when dragging it
  const onScrollbarDragStart = (swiper) => {
    const scrollBar = swiper.scrollbar.el;
    scrollBar.classList.add('sm:sui-opacity-100');
  };
  const onScrollbarDragEnd = (swiper) => {
    const scrollBar = swiper.scrollbar.el;
    scrollBar.classList.remove('sm:sui-opacity-100');
  };

  const classes = classNames('sui-group/sui-carousel sui-relative sui-z-[0] !sui-flex sui-flex-wrap sui-justify-end sui-group [&>.swiper-wrapper]:sui-flex [&>.swiper-wrapper]:sui-mt-1 [&>.swiper-wrapper]:sui-w-full', {
    'sui-mb-8': !disableMargin
  });

  const handleButton = (event) => {
    event.preventDefault();
    event.stopPropagation();
    focusNextElement();
  };

  if (!containerId) {
    return null;
  }
  return (
    <div id={rootId} ref={containerRef} className="sui-relative">
      {header}
      {!isHero && (
        <div className="sui-absolute sui-z-800 sui-top-2 sui-left-2 [clip-path:circle(0%)] focus-within:[clip-path:none]">
          <Button onClick={handleButton}>Skip Listing</Button>
        </div>
      )}
      <Swiper
        modules={[A11y, Autoplay, FreeMode, Mousewheel, Scrollbar, Pagination, Navigation]}
        autoplay={autoplay ? {
          pauseOnMouseEnter: true,
          delay: 5000
        } : false}
        breakpoints={_breakpoints}
        className={classes}
        containerModifierClass="sui-swiper-"
        freeMode={freeModeSettings}
        preventClicks
        preventClicksPropagation
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView + peekAmount}
        slidesPerGroup={slidesPerGroup}
        scrollbar={scrollbarSettings}
        mousewheel={{
          forceToAxis: true
        }}
        a11y={{ notificationClass: ['swiper-notification', 'sui-absolute', 'sui-opacity-0', 'sui-top-0', 'sui-left-0'] }}
        onScrollbarDragStart={onScrollbarDragStart}
        onScrollbarDragEnd={onScrollbarDragEnd}
        loop={loop}
        pagination={paginationSettings}
        style={{
          '--swiper-pagination-color': 'rgb(0, 0, 0)',
          '--swiper-pagination-bottom': paginationPosition === 'outside' ? '-2.1rem' : null,
          '--swiper-pagination-background': hideBulletsBackground ? 'transparent' : null,
        }}
        {...SwiperProps}
      >
        {children.map((slide, index) => {
          return (
            <SwiperSlide
              key={`slide-${index}`}
              className={classNames('sui-shrink-0 [&>*]:sui-h-full sui-touch-pan-y', {
                'hover:sui-shadow-lg sui-transition-shadow': shadow
              })}
              onKeyUp={(event) => onSwiperSlideKeyUp(event, index)}
            >
              {slide}
            </SwiperSlide>
          );
        })}
        {!hideControls && (
          <div slot="container-end" className="sui-flex sui-justify-center sui-items-center">
            <Paginator ref={nextButtonRef} buttonSpacing={buttonSpacing} loop={loop} />
          </div>
        )}
      </Swiper>
    </div>
  );
};

V2Carousel.displayName = 'V2Carousel';

V2Carousel.propTypes = {
  /**
   * The action that goes in the upper right corner of carousel
   */
  action: PropTypes.node,
  /**
   * When true, carousel will autoplay
   * @default false
   */
  autoplay: PropTypes.bool,
  /**
   * Number of slides to display based on breakpoint
   */
  breakpoints: PropTypes.shape({
    sm: PropTypes.shape({
      slidesPerView: PropTypes.number,
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    md: PropTypes.shape({
      slidesPerView: PropTypes.number,
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    lg: PropTypes.shape({
      slidesPerView: PropTypes.number,
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    xl: PropTypes.shape({
      slidesPerView: PropTypes.number,
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    '2xl': PropTypes.shape({
      slidesPerView: PropTypes.number,
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    })
  }),
  /**
   * @ignore
   * Uses the spacing scale. Controls the position of the navigational buttons.
   */
  buttonSpacing: PropTypes.string,
  /**
   * The ID of the container. Used to apply the necessary Carousel styles.
   */
  containerId: PropTypes.string.isRequired,
  /**
   * The carousel slides
   */
  children: PropTypes.node,
  /**
   * Disable the default margin of 32px (mb-8) under the Carousel.
   * When set to true, the Carousel will have no margin below it.
   * @default false
  */
  disableMargin: PropTypes.bool,
  /**
   * Disables the sliding free mode.
  */
  disableFreeMode: PropTypes.bool,
  /**
   * If `true` will hide the carousel controls. (Note that hiding the controls will impact accesibility, especially when
   * the carousel has more than one slide. This prop won't prevent the scrollbar from showing up in these occasions.)
   * @ignore
  */
  hideControls: PropTypes.bool,
  /**
   * Hides the pagination background. @default false
   */
  hideBulletsBackground: PropTypes.bool,
  /**
   * Layout style of the Carousel
   * @default basic
   */
  layout: PropTypes.oneOf(['basic', 'full-bleed']),
  /**
   * When true, carousel will not disable the previous and next paginator buttons.
   * If Carousel is on the first slide and the previous button is clicked, it will paginate to the last group.
   * If Carousel is on the last slide and the next button is clicked, it will paginate to the first group.
   * Additionally this will hide the scrollbar as there is a
   * [known bug](https://github.com/nolimits4web/swiper/issues/2315#issuecomment-343162574) on the internal Swiper
   * library used.
   */
  loop: PropTypes.bool,
  /**
   * Sets the pagination position.
   * @default inside
   */
  paginationPosition: PropTypes.oneOf(['inside', 'outside']),
  /**
   * Determines how much are the Carousel slides allowed to peek. Only works when the Carousel type is set to 'peek'.
   */
  peek: PropTypes.oneOf(['sm', 'md', 'lg']),
  /**
   * Sets the scrollbar position relative to the content.
   */
  scrollbarPosition: PropTypes.oneOf(['inside', 'outside']),
  /**
   * If `true`, enables the slide box-shadow
   */
  shadow: PropTypes.bool,
  /**
   * Number of slides to transition when clicking next/prev
   * @default 1
   */
  slidesPerGroup: PropTypes.number,
  /**
   * Number of slides to display at once
   * @default 2
   */
  slidesPerView: PropTypes.number,
  /**
   * The space between slides
   * @default 16
   */
  spaceBetween: PropTypes.number,
  /**
   * Props applied to the underlying Swiper component
   */
  SwiperProps: PropTypes.object,
  /**
   * Determines how styles are applied and scoped.
   * ´reset´ will help with scoping the Carousel styles, while ´defaults´ will apply core swiper styles.
   */
  swiperStyles: PropTypes.oneOf(['reset', 'default']),
  /**
   * Title of carousel in upper left corner
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Two display types of carousel
   * @default 'peek'
   */
  type: PropTypes.oneOf(['peek', 'peekless']),
  /**
     * The Carousel version to use.
     * @default 'old'
     */
  version: PropTypes.oneOf(['old', 'new'])
};

export { V2Carousel };